import axios from 'axios';

// Temporarily use Railway directly while fixing Supabase Edge Functions
const RAILWAY_URL = 'https://remuvr-api-production.up.railway.app';
const API_URL = RAILWAY_URL; // Use Railway directly for now

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 180000, // Increase timeout to 3 minutes for larger images
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: false // Disable credentials for CORS
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 3000; // 3 seconds

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('API Error Details:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. The server is taking too long to respond. Try with a smaller image or try again later.');
    }
    
    if (error.response) {
      // Use the server's detailed error message if available
      const errorMessage = error.response.data?.detail || error.response.statusText;
      throw new Error(errorMessage);
    }
    
    throw new Error('Network error. The server might be starting up, please try again in a minute.');
  }
);

export const removeBackground = async (file) => {
  // Check file size
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  if (file.size > MAX_FILE_SIZE) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`File size (${fileSizeMB}MB) exceeds maximum limit of 10MB. Please compress your image or try a smaller file.`);
  }

  // Create form data for Edge Function (same as original API)
  const formData = new FormData();
  formData.append('file', file);

  // API key not needed for direct Railway call

  let attempt = 1;
  while (attempt <= MAX_RETRIES) {
    try {
      console.log(`Attempt ${attempt}/${MAX_RETRIES} - Sending request to: ${API_URL}/remove-background`);

      const response = await fetch(`${API_URL}/remove`, {
        method: 'POST',
        body: formData,
        // Remove headers for direct Railway call
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to process image');
      }

      // Railway returns the image directly as a blob
      const blob = await response.blob();

      // Convert blob to base64 data URL for display
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error);
      if (attempt === MAX_RETRIES) {
        throw error;
      }
      console.log(`Retrying in ${RETRY_DELAY}ms...`);
      await sleep(RETRY_DELAY);
      attempt++;
    }
  }
};

export const downloadImage = async (filename) => {
  try {
    const response = await api.get(`/download/${filename}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

export const cleanupImage = async (filename) => {
  try {
    await api.delete(`/cleanup/${filename}`);
  } catch (error) {
    console.error('Cleanup failed:', error);
    // Don't throw error for cleanup failures
  }
};

export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    throw error;
  }
};

export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data.status === 'healthy';
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};
